import {
  FilterSelect,
  Input,
  PageGridContainer,
  Tag,
  TagCloseButton,
  TagText,
  Heading,
  SimpleGrid,
  Flex,
  Button,
  Box,
} from "@govlaunch/web";
import debounce from "lodash/fp/debounce";
import { useRouter } from "next/router";
import React, { useRef, useState, useCallback } from "react";
import useCountryStateFilters from "~/src/shared/hooks/useCountryStateFilters";
import stringOrFirst from "~/src/utils/stringOrFirst";
import TagsFilterSelect from "~/src/shared/components/TagsFilterSelect";
import useTagsFilter from "~/src/shared/hooks/useTagsFilter";
import { appendNextLinkHrefParams } from "~/src/utils/nextLinkHref";
import useIsMobile from "~/lib/hooks/useIsMobile";
import stripUndefined from "~/lib/utils/stripUndefined";
import { FormattedMessage, useIntl } from "react-intl";

export default function StoryPageFilters() {
  const router = useRouter();
  const intl = useIntl();

  const variables = {
    title: stringOrFirst(router.query.title),
    country: stringOrFirst(router.query.country),
    state: stringOrFirst(router.query.state),
    tagSlugs: Array.isArray(router.query.tags) ? router.query.tags : router.query.tags ? [router.query.tags] : [],
  };

  const {
    loading,
    countryOptions,
    stateOptions,
    defaultSelectedCountry,
    defaultSelectedState,
    cantFilterByState,
    placeholders,
  } = useCountryStateFilters({
    country: variables.country,
    state: variables.state,
  });

  const performSearch = useCallback(
    debounce(400, (searchQuery: string) => {
      router.push({
        pathname: router.pathname,
        query: stripUndefined({
          ...router.query,
          title: searchQuery,
        }),
      });
    }),
    [],
  );

  const initialTagSlugs = useRef<string[]>(variables.tagSlugs);
  const { loading: loadingTags, tags } = useTagsFilter({ tags: initialTagSlugs.current });
  const [tagOptions, setTagOptions] = useState(tags);
  const isMobile = useIsMobile();
  const [isShowingFilters, setIsShowingFilters] = useState(!isMobile);

  if (loading || loadingTags) {
    return null;
  }

  return (
    <div>
      <PageGridContainer mt={6} mb={6}>
        <Flex
          align={["flex-start", "flex-start", "center"]}
          direction={["column", "column", "row"]}
          borderBottom={["none", "none", "1px solid #ADB6C3"]}
          pb={[0, 0, 4]}
        >
          <Box mr={3} mb={[4, 4, 0]} width={["100%", "100%", "auto"]}>
            <Heading color="gray.0" m={0} textTransform="uppercase" fontSize="2xl" display="inline-flex" as="h1">
              <FormattedMessage defaultMessage="Stories" id="Ldxym4" />
            </Heading>

            {isMobile && (
              <Button
                ml={3}
                size="sm"
                onClick={() => setIsShowingFilters(!isShowingFilters)}
                variant={isShowingFilters ? "solid" : "secondary"}
                borderColor={isShowingFilters ? "blue.200" : "gray.200"}
                bg={isShowingFilters ? "blue.200" : "white"}
                leftIcon={(props) => (
                  <svg
                    width="17"
                    height="10"
                    viewBox="0 0 17 10"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    {...props}
                  >
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M16.0715 2H0.0715332V0H16.0715V2ZM13.6714 6H2.47144V4H13.6714V6ZM4.87134 10H11.2713V8H4.87134V10Z"
                      fill="#3F4A5A"
                    />
                  </svg>
                )}
              >
                <span>
                  <FormattedMessage defaultMessage="Filters" id="zSOvI0" />
                </span>
              </Button>
            )}
          </Box>

          {isShowingFilters && (
            <div
              css={{
                border: 0,
                clip: "rect(0 0 0 0)",
                height: "1px",
                margin: "-1px",
                overflow: "hidden",
                padding: 0,
                position: "absolute",
                width: "1px",

                // https://medium.com/@jessebeach/beware-smushed-off-screen-accessible-text-5952a4c2cbfe
                whiteSpace: "nowrap",
                wordWrap: "normal",
              }}
            >
              <label id="tags-filter-label">
                <FormattedMessage defaultMessage="Filter by Tags" id="ioiQ9f" />
              </label>
              <label id="country-filter-label">
                <FormattedMessage defaultMessage="Filter by Country" id="AG94Ne" />
              </label>
              <label id="state-filter-label">
                <FormattedMessage defaultMessage="Filter by State" id="jrTpmY" />
              </label>
            </div>
          )}

          {isShowingFilters && (
            <SimpleGrid columns={[1, 2, 4]} spacing={4} flex={1} width={["100%", "100%", "auto"]}>
              <Input
                size="sm"
                aria-label="Search for a story..."
                placeholder={intl.formatMessage({
                  defaultMessage: "Search for a story...",
                  id: "xIp/kP",
                })}
                defaultValue={variables.title}
                onBlur={(event: React.FocusEvent<HTMLInputElement>) => performSearch(event.target.value)}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => performSearch(event.target.value)}
              />

              <TagsFilterSelect
                tagTypes={["STORY", "AWARD"]}
                id="tags-filter"
                aria-labelledby={null}
                disabled={loading}
                selectedItems={tagOptions}
                onChange={(tags) => {
                  setTagOptions(tags);

                  router.push(
                    appendNextLinkHrefParams(router, {
                      tags: tags.map((tag) => tag.value),
                    }),
                  );
                }}
              />

              <FilterSelect
                id="country-filter"
                getMenuProps={() => ({
                  "aria-label": "Filter by Country",
                  "aria-labelledby": null,
                })}
                getInputProps={() => ({
                  "aria-label": "Filter by Country",
                  "aria-labelledby": null,
                })}
                placeholder={placeholders.country}
                items={countryOptions}
                selectedItem={defaultSelectedCountry}
                onChange={(selectedItem) => {
                  router.push(
                    appendNextLinkHrefParams(router, {
                      country: selectedItem?.value,
                      state: null,
                    }),
                  );
                }}
              />

              <FilterSelect
                id="state-filter"
                getMenuProps={() => ({
                  "aria-label": "Filter by State",
                  "aria-labelledby": null,
                })}
                getInputProps={() => ({
                  "aria-label": "Filter by State",
                  "aria-labelledby": null,
                })}
                placeholder={placeholders.state}
                items={stateOptions}
                disabled={cantFilterByState}
                selectedItem={defaultSelectedState}
                onChange={(selectedItem) => {
                  router.push(
                    appendNextLinkHrefParams(router, {
                      state: selectedItem?.value,
                    }),
                  );
                }}
              />
            </SimpleGrid>
          )}
        </Flex>
      </PageGridContainer>

      {isShowingFilters && tagOptions.length > 0 && (
        <PageGridContainer mb={4}>
          <div>
            {tagOptions
              .filter((tag) => variables.tagSlugs.includes(tag.value))
              .map((tag) => (
                <Tag key={tag.value} selected={true} mr={3} mb={2}>
                  <TagText>{tag.name}</TagText>
                  <TagCloseButton
                    onClick={() => {
                      setTagOptions(tagOptions.filter((maybeTag) => maybeTag.value !== tag.value));

                      router.push(
                        appendNextLinkHrefParams(router, {
                          tags: tagOptions.filter((maybeTag) => maybeTag.value !== tag.value).map((tag) => tag.value),
                        }),
                      );
                    }}
                  />
                </Tag>
              ))}
          </div>
        </PageGridContainer>
      )}
    </div>
  );
}
